import React, { FC } from 'react'
import { BrandIconProps } from './BrandIcon.types'
import { BrandIconSize } from './BrandIconSize.type'
import { BrandIconMode } from './BrandIconMode.type'
import { setTextAlignment } from '../../../../../utils/text-util'
import { APIENDPOINT, getCWPEnvironmentForURL } from '../../../../../utils/common-utils'
import { brandIconSize, setFillThemeVarColor } from '../../../../../utils/brand-icons-utils'
import { RTECompound } from '../../RTECompound'
import { pascalCase } from '../../../../../utils/pascalCase'
import './BrandIcon.module.scss'

export const BRAND_ICON_SPRITE_PATH = 'content/gallery/ampmicrosites/brand-icons/icon-sprite.svg'

export const BrandIcon: FC<BrandIconProps> = props => {
  const {
    iconsDropdown,
    iconsColorsDropdown,
    stylesDropdown,
    ariaLabel,
    alignment,
    enableBorder,
    className,
    size,
    identifier,
    testId,
    shouldGrow,
    label,
    brandIconsFullWidth,
  } = props

  const env = getCWPEnvironmentForURL(window.location.href)

  // Use default color Berry if undefined/empty
  const DEFAULT_COLOR = 'berry'
  const normalizedColor = typeof iconsColorsDropdown === 'string' ? iconsColorsDropdown.trim() : ''
  const colorKey = normalizedColor.length > 0 ? normalizedColor : DEFAULT_COLOR
  const isUtilityColor = colorKey.startsWith('u-')

  const toBool = (v?: boolean | string) =>
    typeof v === 'string' ? v.toLowerCase() === 'true' : !!v

  const _enableBorder = toBool(enableBorder)

  const STYLE_TO_MODE: Record<string, BrandIconMode> = {
    'brand-icons-fill-colors': 'fill',
    'brand-icons-stroke-colors': 'stroke',
    'brand-icons-background-colors': 'background',
  }

  const resolveModeFromStyles = (styles?: string): BrandIconMode =>
    STYLE_TO_MODE[styles ?? ''] ?? 'fill'

  const resolvedMode = resolveModeFromStyles(stylesDropdown)

  const colorClassTokens = () => {
    if (isUtilityColor) return [colorKey]
    const BASE = pascalCase(colorKey)
    const prefix = BASE === 'AmpfBlue' ? '' : 'Color'

    switch (resolvedMode) {
      case 'stroke':
        return [`u-stroke${prefix}${BASE}`]
      case 'background':
        return [`u-bgColor${BASE}`, 'u-fillColorWhite']
      case 'fill':
      default:
        return [`u-fill${prefix}${BASE}`]
    }
  }

  const getModeClassNames = () => {
    if (resolvedMode === 'background') {
      return ['BrandIcon--withBgColor u-fillColorWhite']
    }

    return [_enableBorder ? 'BrandIcon--withFillColorBorder' : 'BrandIcon--default']
  }

  const getIconClassNames = () => {
    const colors = colorClassTokens()
    const mode = getModeClassNames()
    return [
      'BrandIconsComponent--icon',
      mode,
      brandIconSize((size as BrandIconSize) || BrandIconSize.default),
      ...colors,
    ]
      .filter(Boolean)
      .join(' ')
  }

  const spriteHref = () => {
    if (!iconsDropdown) return ''
    const baseGallery = APIENDPOINT(env).baseGallery
    return `${baseGallery}${BRAND_ICON_SPRITE_PATH}#${iconsDropdown}`
  }

  const dynamicWidth = brandIconsFullWidth ? 'u-sizeFull' : 'u-block'

  const setBrandIconBorder = () => {
    if (_enableBorder) {
      return (
        <style
          type='text/css'
          dangerouslySetInnerHTML={{
            __html: `
            #icon-${identifier} .BrandIconsComponent--icon { 
              border: 2px solid ${setFillThemeVarColor(colorKey ?? '')}; 
            }
          `,
          }}
        />
      )
    }
    return null
  }

  return (
    <>
      {iconsDropdown && (
        <div
          className={[
            setTextAlignment(alignment ?? ''),
            'BrandIconsComponent',
            'u-paddingBottom20',
            dynamicWidth,
            className,
            shouldGrow && 'u-flexGrow',
          ]
            .filter(Boolean)
            .join(' ')}
          id={`icon-${identifier}`}
          data-testid={testId ? `${testId}-icon` : null}
        >
          <div className='u-flexInline u-flexAlignItemsCenter'>
            {setBrandIconBorder()}
            <svg
              className={getIconClassNames()}
              aria-hidden={ariaLabel ? undefined : 'true'}
              aria-label={ariaLabel || undefined}
              preserveAspectRatio='xMinYMin meet'
              focusable='false'
              data-testid={testId ? `${testId}-svg` : undefined}
            >
              <use href={spriteHref()} data-testid={testId ? `${testId}-svg-use` : undefined} />
            </svg>
            {label && (
              <div className={'u-paddingLeft8'}>
                <RTECompound {...label} />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}
